import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCursorPagination } from '@frameio/graphql-client';
import { useGetCollectionsForProjectQuery } from '@frameio/graphql-client/generated';
import {
  useFolderPermissions,
  useProjectPermissions,
} from '@frameio/permissions';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import { useIsUploadLinksEnabled } from '@frameio/upload-links';
import type { ProjectTreeProps } from '../ProjectTree';
import type {
  ProjectTreeItem,
  ProjectTreeItemType,
  WindowedProjectTree,
} from '../types';
import { useProjectFolders } from './useProjectFolders';

export function useProjectTree(
  rootFolderId: string | undefined,
  skipAncestors: boolean,
  initialItem: ProjectTreeItem | undefined
) {
  const projectId = useActiveProjectId();
  const isIntegrationClient = useIsIntegrationClient();
  const isUploadLinksEnabled = useIsUploadLinksEnabled();
  const [collectionsExpanded, setCollectionsExpanded] = useState(
    initialItem?.type === 'collection'
  );
  const prevInitialItem = useRef(initialItem);

  const {
    fetchMoreFolders,
    isLoading: foldersLoading,
    toggleFolder,
    tree: folderTree,
  } = useProjectFolders(rootFolderId, skipAncestors);

  useEffect(() => {
    if (
      initialItem?.type === 'collection' &&
      prevInitialItem.current?.id !== initialItem.id &&
      !collectionsExpanded
    ) {
      setCollectionsExpanded(true);
    }

    prevInitialItem.current = initialItem;
  }, [collectionsExpanded, initialItem]);

  const {
    data: collectionsData,
    loading: collectionsLoading,
    previousData: prevCollectionsData,
    fetchNext,
  } = useCursorPagination(
    useGetCollectionsForProjectQuery({
      skip: !projectId || isIntegrationClient,
      variables: {
        projectId,
        order: {
          direction: 'DESC',
          field: 'INSERTED_AT',
        },
        page: {
          first: COLLECTIONS_PAGE_SIZE,
        },
      },
    })
  );
  const collections = collectionsData?.account?.project?.collections;

  const canUserDelete =
    useProjectPermissions({
      check: 'canDeleteAsset',
      projectId,
    }) ?? false;

  const canCreateChildren = useFolderPermissions({
    check: 'canCreateChildren',
    folderId: rootFolderId,
    skip: !isUploadLinksEnabled,
  });

  const fetchMoreCollections = useCallback(() => {
    if (!collections || !collections.pageInfo.hasNextPage) return;

    fetchNext({
      count: COLLECTIONS_PAGE_SIZE,
      pageInfo: collections.pageInfo,
    });
  }, [collections, fetchNext]);

  const tree = useMemo<WindowedProjectTree>(() => {
    const finalTree: WindowedProjectTree = {
      children: [folderTree],
      isOpen: true,
      props: {
        id: 'hidden-root',
        type: 'hidden-root',
      },
    };

    if (finalTree.children[0].props?.type === 'root-folder') {
      finalTree.children[0].props.expandable = true;
    }

    // collections and c2c connections pages only exist in the web app, so
    // linking to them from the integrations panel would 404
    if (!isIntegrationClient) {
      finalTree.children.push({
        children:
          collections?.nodes.map(({ id }, index) => ({
            children: [],
            props: {
              id,
              isBoundary: Boolean(
                collections?.pageInfo.hasNextPage &&
                  index === collections.nodes.length - 1
              ),
              type: 'collection',
            },
          })) ?? [],
        isOpen: collectionsExpanded,
        props: {
          id: 'all-collections',
          type: 'all-collections',
        },
      });

      if (isUploadLinksEnabled && canCreateChildren) {
        finalTree.children.push({
          children: [],
          props: {
            id: 'upload-links',
            type: 'upload-links',
          },
        });
      }

      finalTree.children.push({
        children: [],
        props: {
          id: 'c2c-connections',
          type: 'c2c-connections',
        },
      });
    }

    if (canUserDelete && !isIntegrationClient) {
      finalTree.children.push({
        children: [],
        props: {
          id: 'trash',
          type: 'trash',
        },
      });
    }

    return finalTree;
  }, [
    canCreateChildren,
    canUserDelete,
    collections,
    collectionsExpanded,
    folderTree,
    isIntegrationClient,
    isUploadLinksEnabled,
  ]);

  const fetchMore = useCallback(
    (type: ProjectTreeItemType, parentId?: string) => {
      if (type === 'folder' && parentId) {
        fetchMoreFolders(type, parentId);
      }

      if (type === 'collection') {
        fetchMoreCollections();
      }
    },
    [fetchMoreCollections, fetchMoreFolders]
  );

  const toggleItem: ProjectTreeProps['toggleItem'] = (item, opened) => {
    if (item.type === 'folder' || item.type === 'root-folder') {
      toggleFolder(item, opened);
      return;
    }

    if (item.type === 'all-collections') {
      setCollectionsExpanded(opened);
    }
  };

  return {
    fetchMore,
    loading: foldersLoading || (collectionsLoading && !prevCollectionsData),
    toggleItem,
    tree,
  };
}

const COLLECTIONS_PAGE_SIZE = 100;
